import { Box, Button, Drawer, Typography } from "@mui/material";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { isReportableStatus } from "../constants";
import { colors, spacing, typography } from "../../../../assets/styles/tokens";

interface ReportLeadsModalMobileProps {
  data: LeadsManagementData;
}

const TEXT_SX = {
  fontFamily: typography.Font_Family.$value,
  letterSpacing: "0%",
};

const BUTTON_SX = {
  ...TEXT_SX,
  flex: 1,
  height: 40,
  textTransform: "none",
  fontWeight: typography.Font_weight.Semibold.$value,
  fontSize: typography.Size.Text.md.$value,
  lineHeight: typography.Line_height.text.md.$value,
  borderRadius: spacing.Radius.xs.$value,
  boxShadow: "none",
};

// Rounded blue-grey "i" badge from the mockup. Drawn as SVG rather than the
// ℹ️ emoji so it looks the same on every platform.
function InfoBadgeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id="report-info-badge-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#9DBBD6" />
          <stop offset="1" stopColor="#5F87AB" />
        </linearGradient>
      </defs>
      <rect x="0.75" y="0.75" width="18.5" height="18.5" rx="4.5" fill="url(#report-info-badge-fill)" stroke="#4D7396" strokeWidth="1.5" />
      <circle cx="10" cy="5.6" r="1.5" fill="#FFFFFF" />
      <rect x="8.6" y="8.2" width="2.8" height="7.6" rx="0.6" fill="#FFFFFF" />
    </svg>
  );
}

// Bottom-sheet counterpart of Leads/ReportLeadsModal.tsx — same data and
// handlers, laid out like RejectLeadsModalMobile with a brand-colored notice.
export default function ReportLeadsModalMobile({ data }: ReportLeadsModalMobileProps) {
  const { showConfirm, selectedLeads, cancelBulkReport, confirmBulkReport, isReporting, canReportLeads } = data;

  const reportableSelected = selectedLeads.filter((lead) => isReportableStatus(lead.status));
  const brandCounts = new Map<string, number>();
  reportableSelected.forEach((lead) => {
    brandCounts.set(lead.brand, (brandCounts.get(lead.brand) ?? 0) + 1);
  });
  const brandBreakdown = Array.from(brandCounts.entries())
    .map(([brand, count]) => ({ brand, count }))
    .sort((a, b) => b.count - a.count);

  const noticeColor = colors.Brand[600].$value;
  const rowTextSx = {
    ...TEXT_SX,
    fontSize: typography.Size.Text.md.$value,
    lineHeight: typography.Line_height.text.md.$value,
    color: colors.GrayLightMode[900].$value,
  };

  return (
    <Drawer
      anchor="bottom"
      open={showConfirm}
      onClose={cancelBulkReport}
      // Opened from the full-screen detail Dialog (zIndex.modal), which would
      // otherwise cover a Drawer (zIndex.drawer sits below modal).
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      slotProps={{
        paper: {
          sx: {
            borderTopLeftRadius: spacing.Radius.xl.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            px: spacing.Spacing.xl.$value,
            pt: spacing.Spacing.xl.$value,
            pb: spacing.Spacing["4xl"].$value,
          },
        },
      }}
    >
      <Box
        sx={{
          border: `1px solid ${colors.Brand[400].$value}`,
          borderRadius: spacing.Radius.xs.$value,
          bgcolor: colors.Brand[25].$value,
          py: spacing.Spacing.md.$value,
          px: spacing.Spacing.lg.$value,
          textAlign: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.Spacing.xs.$value }}>
          <InfoBadgeIcon />
          <Typography
            sx={{
              ...TEXT_SX,
              fontWeight: 500,
              fontSize: typography.Size.Text.md.$value,
              lineHeight: typography.Line_height.text.md.$value,
              color: noticeColor,
            }}
          >
            Reporting to Brand
          </Typography>
        </Box>
        <Typography
          sx={{
            ...TEXT_SX,
            fontWeight: 400,
            fontSize: typography.Size.Text.sm.$value,
            lineHeight: typography.Line_height.text.sm.$value,
            color: noticeColor,
            mt: spacing.Spacing.xs.$value,
          }}
        >
          Reported leads cannot be undone.
        </Typography>
      </Box>

      <Box sx={{ mt: spacing.Spacing["4xl"].$value, maxHeight: 240, overflowY: "auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: spacing.Spacing.md.$value,
            py: spacing.Spacing.md.$value,
            borderBottom: `1px solid ${colors.GrayLightMode[200].$value}`,
          }}
        >
          <Typography sx={{ ...rowTextSx, fontWeight: 500 }}>Brand Name</Typography>
          <Typography sx={{ ...rowTextSx, fontWeight: 500 }}>Leads</Typography>
        </Box>
        {brandBreakdown.map(({ brand, count }, index) => (
          <Box
            key={brand}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: spacing.Spacing.md.$value,
              py: spacing.Spacing.md.$value,
              borderTop: index === 0 ? "none" : `1px solid ${colors.GrayLightMode[200].$value}`,
            }}
          >
            <Typography sx={{ ...rowTextSx, fontWeight: 400 }}>{brand}</Typography>
            <Typography sx={{ ...rowTextSx, fontWeight: 400 }}>{count}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", gap: spacing.Spacing.xl.$value, mt: spacing.Spacing["5xl"].$value }}>
        <Button
          onClick={cancelBulkReport}
          disabled={isReporting}
          sx={{ ...BUTTON_SX, color: colors.Brand[600].$value, "&:hover": { bgcolor: colors.Brand[25].$value } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={confirmBulkReport}
          disabled={!canReportLeads || isReporting || reportableSelected.length === 0}
          data-tracker="930"
          sx={{
            ...BUTTON_SX,
            bgcolor: colors.Brand[600].$value,
            "&:hover": { bgcolor: colors.Brand[700].$value, boxShadow: "none" },
            // Keep the in-progress label legible on the brand fill.
            ...(isReporting && {
              "&.Mui-disabled": { color: colors.Base.White.$value, bgcolor: colors.Brand[600].$value },
            }),
          }}
        >
          {isReporting ? "Reporting…" : "Confirm"}
        </Button>
      </Box>
    </Drawer>
  );
}
